import React, { createContext, useCallback, useContext, useState } from 'react';
import { Notification } from '../types';

interface INotificationsContext {
  notifications: Notification[];
  addNotification: (arg0: Notification) => void;
  removeNotification: (arg0: Notification, fast?: boolean) => void;
  clearNotifications: () => void;
}
const NotificationsContext = createContext<INotificationsContext>({
  notifications: [],
  addNotification: () => {
    throw new Error('Not implemented');
  },
  removeNotification: () => {
    throw new Error('Not implemented');
  },
  clearNotifications: () => {
    throw new Error('Not implemented');
  },
});

const AUTOREMOVE_NOTIFICATIONS_TIMEOUT = 15000;

const useNotifications = (): INotificationsContext =>
  useContext(NotificationsContext);

export const NotificationsProvider: React.FC = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback(
    (notification: Notification, fast?: boolean) => {
      if (fast) {
        setNotifications((nots) => nots.filter((it) => it !== notification));
      } else {
        const removingNotification = { ...notification, removing: true };
        setNotifications((nots) =>
          nots.reduce(
            (acc, curr) => [
              ...acc,
              ...(curr === notification ? [removingNotification] : [curr]),
            ],
            []
          )
        );
        setTimeout(() => {
          removeNotification(removingNotification, true);
        }, 1000);
      }
    },
    [setNotifications]
  );

  const addNotification = useCallback(
    (notification: Notification) => {
      notification.id = `${Date.now()}`;
      const timeout =
        notification.timeout > 1000
          ? notification.timeout
          : AUTOREMOVE_NOTIFICATIONS_TIMEOUT;
      setNotifications((nots) => [...nots, notification]);
      setTimeout(() => {
        removeNotification(notification);
      }, timeout - 1000);
    },
    [setNotifications]
  );

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, [setNotifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export default useNotifications;
